import { Event } from "@clockwise/design-system/icons";
import { EventMention } from "@clockwise/schema";
import { UserAvatar } from "@clockwise/web-commons/src/ui/UserAvatar";
import { Portal, Transition } from "@headlessui/react";
import classNames from "classnames";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { usePopper } from "react-popper";
import { EventCardAttendee } from "../../event-card/types";
import { formatEventMentionStartTime } from "../util/formatEventMentionStartTime";

const ABOVE_EVERYTHING = 10000;

export type ChatMentionAutoCompleteProps = {
  isOpen: boolean;
  personSuggestions: EventCardAttendee[];
  eventSuggestions: EventMention[];
  onAddSuggestion: (eventSearchValue?: EventMention, personSearchValue?: EventCardAttendee) => void;
  highlightedSuggestion: number | null;
  setHighlightedSuggestion: (index: number) => void;
  referenceElement: React.RefObject<HTMLDivElement>;
};

const EventSuggestions = ({
  suggestions,
  onClick,
  onHover,
  highlightedSuggestion,
  getEventIndex,
}: {
  suggestions: EventMention[];
  onClick: (eventSearchValue?: EventMention, personSearchValue?: EventCardAttendee) => void;
  onHover: (index: number) => void;
  highlightedSuggestion: number | null;
  getEventIndex: (index: number) => number;
}) => {
  return (
    <div>
      <div className="cw-caption cw-font-bold cw-mb-1 cw-text-muted cw-px-1"> Events </div>
      {suggestions.map((eventSuggestion, index) => {
        const globalIndex = getEventIndex(index);

        return (
          <div
            tabIndex={0}
            onMouseEnter={() => {
              onHover(globalIndex);
            }}
            className={classNames(
              "cw-p-1 cw-cursor-pointer cw-flex cw-items-center cw-justify-between cw-rounded cw-mb-1",
              { "cw-bg-default-hover": highlightedSuggestion === globalIndex },
            )}
            onMouseDown={() => {
              onClick(eventSuggestion);
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                onClick(eventSuggestion);
              }
            }}
            key={`${eventSuggestion.externalEventId}-${index}`}
          >
            <div className="cw-flex cw-items-center cw-overflow-hidden">
              <Event className="cw-text-muted cw-w-5 cw-h-5 cw-mx-1" />
              <span className="cw-font-medium cw-ml-2 cw-mr-1 cw-text-ellipsis cw-whitespace-nowrap cw-overflow-hidden">
                {eventSuggestion.title}
              </span>
            </div>
            <span className="cw-text-neutral-muted cw-body-sm cw-min-w-[100px]">
              {formatEventMentionStartTime(eventSuggestion.startTime)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const PersonSuggetions = ({
  suggestions,
  onClick,
  onHover,
  highlightedSuggestion,
}: {
  suggestions: EventCardAttendee[];
  onClick: (eventSearchValue?: EventMention, personSearchValue?: EventCardAttendee) => void;
  onHover: (index: number) => void;
  highlightedSuggestion: number | null;
}) => {
  return (
    <div>
      <div className="cw-caption cw-font-bold cw-my-1 cw-text-muted cw-px-1"> Users </div>
      {suggestions.map((suggestion, index) => {
        return (
          <div
            tabIndex={0}
            onMouseEnter={() => {
              onHover(index);
            }}
            className={classNames(
              "cw-py-1 cw-px-2 cw-cursor-pointer cw-flex cw-items-center cw-rounded cw-mb-1",
              { "cw-bg-default-hover": highlightedSuggestion === index },
            )}
            onMouseDown={() => {
              onClick(undefined, suggestion);
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                onClick(undefined, suggestion);
              }
            }}
            key={`${suggestion.primaryCalendar}-${index}`}
          >
            <UserAvatar
              profile={
                suggestion?.profile || { familyName: "", givenName: "", externalImageUrl: "" }
              }
              size="small"
            />
            <span className="cw-font-medium cw-ml-2 cw-mr-1">{`${
              suggestion.profile?.givenName || ""
            } ${suggestion.profile?.familyName || ""}`}</span>
            <span className="cw-text-neutral-muted cw-body-sm">{suggestion.primaryCalendar}</span>
          </div>
        );
      })}
    </div>
  );
};

export const ChatMentionAutoComplete = ({
  isOpen,
  personSuggestions,
  eventSuggestions,
  onAddSuggestion,
  highlightedSuggestion,
  setHighlightedSuggestion,
  referenceElement,
}: ChatMentionAutoCompleteProps) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement.current, container, {
    placement: "top-start",
    strategy: "fixed",
    modifiers: [
      {
        name: "flip",
        enabled: true,
      },
      { name: "offset", options: { offset: [0, 4] } },
    ],
  });

  const getEventIndex = (eventIndex: number) => {
    return eventIndex + personSuggestions.length;
  };

  const showPersonSuggestions = !isEmpty(personSuggestions);
  const showEventSuggestions = !isEmpty(eventSuggestions);

  return (
    <Portal>
      <Transition
        show={isOpen}
        leave="cw-transition cw-ease-in cw-duration-100"
        leaveFrom="cw-opacity-100"
        leaveTo="cw-opacity-0"
        enter="cw-transition cw-ease-in cw-duration-75"
        enterFrom="cw-opacity-0"
        enterTo="cw-opacity-100"
      >
        <div
          ref={setContainer}
          style={{
            ...styles.popper,
            minWidth: referenceElement.current?.scrollWidth || 0,
            maxWidth: 500,
            zIndex: ABOVE_EVERYTHING,
          }}
          {...attributes.popper}
          className={classNames(
            "cw-p-2 cw-shadow-md cw-rounded-lg cw-absolute cw-body-base cw-bg-default",
            "cw-border cw-border-solid cw-border-default",
          )}
        >
          {showPersonSuggestions && (
            <PersonSuggetions
              suggestions={personSuggestions}
              onClick={onAddSuggestion}
              onHover={setHighlightedSuggestion}
              highlightedSuggestion={highlightedSuggestion}
            />
          )}
          {showEventSuggestions && (
            <EventSuggestions
              suggestions={eventSuggestions}
              onClick={onAddSuggestion}
              onHover={setHighlightedSuggestion}
              highlightedSuggestion={highlightedSuggestion}
              getEventIndex={getEventIndex}
            />
          )}
        </div>
      </Transition>
    </Portal>
  );
};
